import classNames from 'classnames';
import { Warning, CheckmarkFilled } from '@carbon/icons-react';
import { FormattedCount } from 'components/formatted-count';
import type { FactorChipItem } from '../../types';
import { MATCHED_FACTOR_CHIP_LIMIT } from '../../utils';
import styles from './factor-chips.module.scss';

interface FactorChipsProps {
  factors: FactorChipItem[];
}

/**
 * The changed/matched factor chips under the Step 2 tiles: every changed factor
 * (warning-toned), then the matched factors (success-toned) capped at
 * {@link MATCHED_FACTOR_CHIP_LIMIT} with a "+N more" chip for the remainder.
 *
 * @param {FactorChipsProps} props - The factor inventory from the analysis response.
 * @return {JSX.Element | null} The chip groups, or null when there are no factors.
 */
export function FactorChips({ factors }: FactorChipsProps) {
  if (factors.length === 0) return null;

  const changed = factors.filter((factor) => factor.changed);
  const matched = factors.filter((factor) => !factor.changed);
  const shownMatched = matched.slice(0, MATCHED_FACTOR_CHIP_LIMIT);
  const overflow = matched.length - shownMatched.length;

  return (
    <div className={styles.groups}>
      {changed.length > 0 && (
        <div>
          <div className={styles.heading}>Changed Factors</div>
          <div className={styles.chips}>
            {changed.map((factor) => (
              <span
                key={factor.key}
                aria-label={`${factor.label}, changed`}
                className={classNames(styles.chip, styles.chipChanged)}
              >
                <Warning size={16} aria-hidden className={styles.iconChanged} />
                {factor.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {matched.length > 0 && (
        <div>
          <div className={styles.heading}>
            Matched Factors (<FormattedCount value={matched.length} />)
          </div>
          <div className={styles.chips}>
            {shownMatched.map((factor) => (
              <span
                key={factor.key}
                aria-label={`${factor.label}, matched`}
                className={classNames(styles.chip, styles.chipMatched)}
              >
                <CheckmarkFilled
                  size={16}
                  aria-hidden
                  className={styles.iconMatched}
                />
                {factor.label}
              </span>
            ))}
            {overflow > 0 && (
              <span className={classNames(styles.chip, styles.chipMore)}>
                +<FormattedCount value={overflow} /> more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
